import { ArrowLeftOutlined } from "@ant-design/icons"
import { Alert, Button, Card, Modal, Tabs, Tag } from "antd"
import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector"
import { RoutesPaths } from "../../../constants/routes.ts"
import * as services from "../api/services.ts"
import { fetchBranchDetail } from "../redux/branches.slice.ts"
import { BranchConfigurationTab } from "./tabs/BranchConfigurationTab.tsx"
import { BranchOverviewTab } from "./tabs/BranchOverviewTab.tsx"
import { BranchTeamTab } from "./tabs/BranchTeamTab.tsx"

export default function SettingsBranchDetailPage() {
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const detail = useAppSelector((state) => state.branches.detail)
  const fetching = useAppSelector((state) => state.branches.fetchingDetail)
  const [archiveModalOpen, setArchiveModalOpen] = useState(false)
  const [archivePreview, setArchivePreview] = useState<{
    can_archive: boolean
    blockers: Record<string, number>
  } | null>(null)

  useEffect(() => {
    if (id) dispatch(fetchBranchDetail(id))
  }, [id, dispatch])

  const refresh = () => {
    if (id) dispatch(fetchBranchDetail(id))
  }

  const openArchiveModal = async () => {
    if (!id) return
    const response = await services.getArchivePreview(id)
    if (response.ok) setArchivePreview(response.data)
    setArchiveModalOpen(true)
  }

  const confirmArchive = async () => {
    if (!id) return
    await services.archiveBranch(id)
    setArchiveModalOpen(false)
    refresh()
  }

  const restore = async () => {
    if (!id) return
    await services.restoreBranch(id)
    refresh()
  }

  // Only the first load shows the skeleton — a later refetch (after saving a
  // configuration change, archiving, etc.) must not unmount <Tabs> under the
  // user, which would reset it back to its defaultActiveKey.
  if (!detail) return <Card loading={fetching} className="m-4" />

  const blockers = archivePreview?.blockers || {}
  const blockerLabels: Record<string, string> = {
    active_leads: "active leads",
    active_projects: "active projects",
    unpaid_invoices: "unpaid invoices",
    upcoming_events: "upcoming calendar events"
  }

  return (
    <div className="p-4">
      <Button
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(RoutesPaths.settings.branches)}
        className="mb-2 px-0"
      >
        Back to Branches
      </Button>

      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            {detail.name}
            <Tag color={detail.is_active ? "green" : "default"}>
              {detail.is_active ? "Active" : "Archived"}
            </Tag>
            {detail.is_default_branch && <Tag color="blue">Default</Tag>}
          </h2>
          <p className="text-gray-500 text-sm">
            Manage this branch and its configuration
          </p>
        </div>
        {detail.is_active ? (
          <Button danger onClick={openArchiveModal}>
            Archive branch
          </Button>
        ) : (
          <Button onClick={restore}>Restore branch</Button>
        )}
      </div>

      <Tabs
        defaultActiveKey="overview"
        items={[
          {
            key: "overview",
            label: "Overview",
            children: <BranchOverviewTab branch={detail} onSaved={refresh} />
          },
          {
            key: "team",
            label: "Team & access",
            children: id && <BranchTeamTab branchId={id} />
          },
          {
            key: "configuration",
            label: "Configuration",
            children: id && (
              <BranchConfigurationTab branchId={id} onSaved={refresh} />
            )
          }
        ]}
      />

      <Modal
        title="Archive branch"
        open={archiveModalOpen}
        onCancel={() => setArchiveModalOpen(false)}
        onOk={confirmArchive}
        okButtonProps={{ disabled: !archivePreview?.can_archive, danger: true }}
        okText="Archive"
      >
        {archivePreview?.can_archive ? (
          <p>Archiving excludes this branch from new assignments and intake. Historical records stay accessible read-only, and it can be restored later.</p>
        ) : (
          <Alert
            type="warning"
            showIcon
            message="This branch has unresolved work"
            description={
              <ul className="list-disc pl-4">
                {Object.entries(blockers)
                  .filter(([, count]) => count > 0)
                  .map(([key, count]) => (
                    <li key={key}>
                      {count} {blockerLabels[key] || key}
                    </li>
                  ))}
              </ul>
            }
          />
        )}
      </Modal>
    </div>
  )
}
